import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, MapPin } from 'lucide-react';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { formatPrice } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function AdminPropertiesPage({ user }) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;

    try {
      await axios.delete(`${API}/properties/${propertyId}`, {
        withCredentials: true
      });
      toast.success('Property deleted successfully');
      fetchProperties();
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error('Failed to delete property');
    }
  };

  return (
    <AdminLayout user={user}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-4xl font-medium text-primary mb-2">Properties</h1>
            <p className="text-[#57534E]">Manage your property listings</p>
          </div>
          <Link to="/admin-portal/properties/new">
            <Button
              data-testid="add-property-button"
              className="bg-primary text-white hover:bg-primary/90 rounded-sm font-medium"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Property
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : properties.length > 0 ? (
          <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FFFBF5] border-b border-stone-200">
                  <tr>
                    <th className="text-left p-4 font-medium text-primary">Property</th>
                    <th className="text-left p-4 font-medium text-primary">Type</th>
                    <th className="text-left p-4 font-medium text-primary">Location</th>
                    <th className="text-left p-4 font-medium text-primary">Price</th>
                    <th className="text-left p-4 font-medium text-primary">Status</th>
                    <th className="text-right p-4 font-medium text-primary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {properties.map((property) => (
                    <tr key={property.property_id} className="border-b border-stone-100 hover:bg-[#FFFBF5]">
                      <td className="p-4">
                        <div className="flex items-center gap-4">
                          <img
                            src={property.images[0] || 'https://images.unsplash.com/photo-1663756915301-2ba688e078cf?crop=entropy&cs=srgb&fm=jpg&q=85'}
                            alt={property.title}
                            className="w-16 h-16 object-cover rounded-sm"
                          />
                          <div>
                            <p className="font-medium text-primary">{property.title}</p>
                            <p className="text-sm text-[#57534E]">{property.category}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-[#57534E] capitalize">{property.property_type}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-1 text-[#57534E]">
                          <MapPin className="w-4 h-4" />
                          <span className="text-sm">{property.city}</span>
                        </div>
                      </td>
                      <td className="p-4 font-medium text-primary">
                        {formatPrice(property.price)}
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${property.status === 'available' ? 'bg-green-100 text-green-800' :
                          property.status === 'sold' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                          {property.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/admin-portal/properties/edit/${property.property_id}`}>
                            <Button
                              data-testid={`edit-property-${property.property_id}`}
                              size="sm"
                              variant="ghost"
                              className="text-primary hover:bg-primary/10"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button
                            data-testid={`delete-property-${property.property_id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(property.property_id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
            <p className="text-[#57534E] mb-4">No properties found. Add your first property!</p>
            <Link to="/admin-portal/properties/new">
              <Button
                data-testid="add-first-property-button"
                className="bg-primary text-white hover:bg-primary/90 rounded-sm font-medium"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Property
              </Button>
            </Link>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}